import {InputDate} from '@/components/commons/input-date/InputDate'
import {InputSelect} from '@/components/commons/input-select/InputSelect'
import {InputTime} from '@/components/commons/input-time/InputTime'
import {TextArea} from '@/components/commons/textarea/TextArea'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {CreateEventFormValidationSchema} from '@/features/agenda/formSchemas'
import {agendaDurationMapToLabel} from '@/features/agenda/utils'
import {InputSelectUser, UserSmallSelection} from '@/features/users/components/input-select-user/InputSelectUser'
import {useTranslation} from '@/translations/i18n'
import {ObjectEntries} from '@/utilities/helpers'
import {useCallback, useMemo} from 'react'
import {Controller, useController, useFormContext, useWatch} from 'react-hook-form'
import {StyledAgendaDateRoomFields} from '../agenda-event-form-fields/style'

const ALL_DAY_DURATION = '720'

export const AgendaEventFormGenericFields = () => {
    const {t} = useTranslation()
    const {control, setValue, clearErrors, register, formState} = useFormContext<CreateEventFormValidationSchema>()

    const targetDate = useWatch({control, name: 'date.date'})
    const duration = useWatch({control, name: 'duration'})
    const consultants = useWatch({control, name: 'consultants'})

    const {
        field: {onChange: onChangeConsultant, ...consultantField},
        fieldState: {error: consultantsError}
    } = useController({
        control,
        name: 'consultants.0',
        defaultValue: {id: '', fullName: ''}
    })

    const handleChangeConsultant = useCallback(
        (value: UserSmallSelection | null) => {
            if (value) {
                onChangeConsultant({
                    ...consultantField.value,
                    id: value?.id,
                    fullName: value?.fullName,
                    group: value?.group,
                    specializationName: value?.specializationName,
                    specializationId: value?.specializationId
                })
            }
        },
        [onChangeConsultant, consultantField.value]
    )

    const handleChangeDurationSideEffects = (newDuration: string | null) => {
        setValue('date.date', undefined)
        if (newDuration === ALL_DAY_DURATION) {
            setValue('date.time', '08:00')
        } else {
            setValue('date.time', '')
        }
    }

    const genericEventDurationOptions = useMemo((): [string, string][] => {
        const baseOptions: [string, string][] = ObjectEntries(agendaDurationMapToLabel).map(([key, value]) => [
            key.toString(),
            t(value, {value: key}).toString()
        ])
        return [...baseOptions, [ALL_DAY_DURATION, t('commons:all_day_availability')]]
    }, [t])

    const handleChangeDateSideEffects = (value: Date | null) => {
        if (value) {
            clearErrors('date')
        }

        if (duration !== ALL_DAY_DURATION) {
            setValue('date.time', '')
        }
    }

    const consultantErrorMessage =
        consultantsError && typeof consultantsError == 'object' && 'id' in consultantsError
            ? (consultantsError.id as Record<string, string>)?.message
            : consultantsError?.message

    return (
        <Flexbox direction="column" gap={4}>
            <InputSelectUser
                {...consultantField}
                onChange={value => handleChangeConsultant(value)}
                label={t('commons:healthcare_professional', {count: 1})}
                placeholder={t('agenda:select_consultant_placeholder')}
                disabledOptions={consultants?.map(consultant => consultant.id) ?? []}
                filters={{
                    status: 'operative'
                }}
                errorMessage={consultantErrorMessage}
            />

            <StyledAgendaDateRoomFields $columns={3}>
                <Controller
                    control={control}
                    name="duration"
                    render={({field, fieldState}) => (
                        <InputSelect
                            label={t('commons:duration')}
                            value={field.value ? field.value : undefined}
                            errorMessage={fieldState.error?.message}
                            onChange={value => {
                                field.onChange(value)
                                handleChangeDurationSideEffects(value)
                            }}
                            optionsEntries={genericEventDurationOptions}
                        />
                    )}
                />

                <Controller
                    control={control}
                    name="date.date"
                    render={({field, fieldState}) => (
                        <InputDate
                            label={t('commons:date')}
                            placeholder="GG/MM/AAAA"
                            errorMessage={fieldState.error?.message ? t('errors:required') : undefined}
                            fullWidth
                            disabled={!duration}
                            value={targetDate ? field.value : null}
                            onChange={value => {
                                field.onChange(value)
                                handleChangeDateSideEffects(value)
                            }}
                        />
                    )}
                />

                <Controller
                    control={control}
                    name="date.time"
                    render={({field, fieldState}) => (
                        <InputTime
                            label={t('agenda:start_time')}
                            value={duration === ALL_DAY_DURATION ? field.value : targetDate ? field.value : ''}
                            errorMessage={fieldState.error?.message}
                            startTime="08:00"
                            endTime="18:45"
                            interval={15}
                            fullWidth
                            disabled={!targetDate || duration === ALL_DAY_DURATION}
                            placeholder={t('agenda:select_time')}
                            onChange={field.onChange}
                        />
                    )}
                />
            </StyledAgendaDateRoomFields>

            <TextArea
                label={t('commons:notes')}
                placeholder={t('agenda:add_notes')}
                helpText={t('agenda:add_notes_placeholder')}
                errorMessage={formState.errors.note?.message}
                {...register('note')}
            />
        </Flexbox>
    )
}
